import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Calendar from './AppointmentFullCalendar/AppointmentFullCalendar';
import CustModal from '../common/custmodal/CustModal';
import AddAvailability from './AddAvailability/addAvailability';
import AvailabilityFullCalendar from './AvailabilityFullCalendar/AvailabilityFullCalendar';
import styles from './Visits.module.css';
import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import { getLocation } from '../../redux/location/actions/location.action';
import { getAvailabilities } from '../../redux/availability/actions/availability.action';
import Loader from '../common/loader/Loader';
import Toast from '../common/toast/Toast';
import { AvailabilityRedirectionContext } from '../context/availabilityRedirection';
import { fetchLockerWiseAvailabilityAndAppointments } from '../../redux/locker-wise-availability/actions/lockerWiseAvailability.actions';
import { LockerWiseAvailabilityAndAppointmentsState } from '../../redux/locker-wise-availability/interfaces/lockerWiseAvailability.interface';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '../auth/login/Login';
import Button from '../common/button/Button';
import HistoryIcon from '../svgIcons/HistoryIcon';
import AppointmentAvailabilityLogs from './AppointmentAvailabilityLogs/AppointmentAvailabilityLogs';
import moment from 'moment';
import { DatesSetArg } from '@fullcalendar/core';
import SearchIcon from '../svgIcons/SearchIcon';
import LockerManageMent from './LockerManageMent/LockerManageMent';
import TinyLoader from '../common/tiny-loader/TinyLoader';
import CameraIcon from '../svgIcons/CameraIcon';
import KioskStatus from './KioskStatus/KioskStatus';
import KeyLockerIcon from '../svgIcons/KeyLockerIcon';
interface AvailabilityList {
    id: string;
    created_at: Date;
    updated_at: Date;
    name: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    adminId: string;
    images: string[];
    timeZone: string;
    description: string;
    confirmText: string;
    provisioningId: number;
    masterCode: string;
    mapUrl: string;
    pmsPropertyId?: string;
    yardiId: string;
    listOfPOI: string;
}

// Hidden at Taylor's request (Oct 19); set true to bring the Kiosk Status button back for the next-gen rollout
const SHOW_KIOSK_STATUS_BUTTON = false;

const Visits = () => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const permissionLoading = useSelector((state: IRootState) => state?.permissionsReducer?.loading);
    const canCreateAvailability = hasPermission('Self-Guided Tours', 'Availability', 'create');
    const canDeleteAvailability = hasPermission('Self-Guided Tours', 'Availability', 'delete');
    const canReadAvailability = hasPermission('Self-Guided Tours', 'Availability', 'read');
    const canUpdateAvailability = hasPermission('Self-Guided Tours', 'Availability', 'update');

    const canCreateAppointment = hasPermission('Self-Guided Tours', 'Appointments', 'create');
    const canDeleteAppointment = hasPermission('Self-Guided Tours', 'Appointments', 'delete');
    const canReadAppointment = hasPermission('Self-Guided Tours', 'Appointments', 'read');
    const canUpdateAppointment = hasPermission('Self-Guided Tours', 'Appointments', 'update');
    const availabilityRedirectionContext = useContext(AvailabilityRedirectionContext);

    const locations = useSelector((state: IRootState) => state.locationReducer.data?.locations);
    const locationsListLoader = useSelector((state: IRootState) => state.locationReducer.loading);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const selectedLocationLockersCount = useSelector<IRootState, LockerWiseAvailabilityAndAppointmentsState>(
        (state) => state.lockerWiseAvailabilityAndAppointments,
    ).lockerCounts;

    const [addAvailability, setAddAvailability] = useState(false);
    const [active, setActive] = useState(canReadAppointment ? 'appointments' : canReadAvailability ? 'availibility' : null);
    const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
    const [locationId, setLocationId] = useState('');
    const [locationName, setLocationName] = useState('');
    const [AvailabilityList, setAvailabilityList]: AnyIfEmpty<object> = useState();
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [showKioskStatusModal, setShowKioskStatusModal] = useState(false);
    const [showLockerManagementModal, setShowLockerManagementModal] = useState(false);
    const [startDate, setStateDate] = useState<string | null>(() => moment().startOf('week').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState<string | null>(() => moment().endOf('week').format('YYYY-MM-DD'));
    const [searchText, setSearchText] = useState('');
    const [isClicked, setIsClicked] = useState(false);
    const fetchedLocationIdRef = useRef<string | null>(null);
    const previousActiveTabRef = useRef<string | null>(null);

    useEffect(() => {
        dispatch(getLocation(undefined, undefined, true));
    }, [dispatch]);

    // Memoize isBMSProperty function
    const isBMSProperty = useCallback((locationId: string) => {
        if (AvailabilityList && locationId) {
            const location = AvailabilityList.find((loc: AvailabilityList) => loc.id === locationId);
            return location?.lockerName?.toLowerCase() === 'bms';
        }
        return false;
    }, [AvailabilityList]);

    // Memoize merged availability list
    const mergedAvailabilityList = useMemo(() => {
        let temp: AvailabilityList[] = [];
        if (invitedLocations?.length || locations?.length) {
            temp = [...(invitedLocations || []), ...(locations || [])];
        }
        return temp;
    }, [invitedLocations, locations]);

    // Update AvailabilityList state only when merged list changes
    useEffect(() => {
        setAvailabilityList(mergedAvailabilityList);
    }, [mergedAvailabilityList]);

    // Set initial location when AvailabilityList is populated
    useEffect(() => {
        if (!locationId && AvailabilityList?.length > 0) {
            setLocationId(AvailabilityList[0].id);
            setLocationName(AvailabilityList[0].name);
        }
    }, [AvailabilityList, locationId]);

    // Prevent duplicate API calls for location data
    useEffect(() => {
        if (locationId && fetchedLocationIdRef.current !== locationId) {
            dispatch(fetchLockerWiseAvailabilityAndAppointments(locationId));
            fetchedLocationIdRef.current = locationId;
        }
    }, [locationId, dispatch]);

    // Clear previous tab's data when switching tabs to prevent showing stale data
    useEffect(() => {
        // When switching away from appointments, clear appointment data immediately
        if (previousActiveTabRef.current === 'appointments' && active === 'availibility') {
            // Clear appointment data without affecting loading state
            dispatch({ type: 'GET_VISITS_CLEAR' });
        }
        // When switching away from availibility, clear availability data immediately
        if (previousActiveTabRef.current === 'availibility' && active === 'appointments') {
            // Clear availability data without affecting loading state
            dispatch({ type: 'GET_AVAILABILITIES_CLEAR' });
        }

        // Update previous tab reference
        if (active) {
            previousActiveTabRef.current = active;
        }
    }, [active, dispatch]);

    // Note: Availability fetching is handled by AvailabilityFullCalendar component
    // No need to fetch here to avoid duplicate API calls

    useEffect(() => {
        if (
            availabilityRedirectionContext?.availabilityData.locationId &&
            availabilityRedirectionContext?.availabilityData.unitId &&
            availabilityRedirectionContext.availabilityData.lockerNumber &&
            availabilityRedirectionContext.availabilityData.selectedDate
        ) {
            setAddAvailability(true);
            setLocationId(availabilityRedirectionContext.availabilityData.locationId);
        }
    }, [availabilityRedirectionContext]);

    const addAvailabilityHandler = useCallback((type: 'add-availability' | 'make-unit-unavailable') => {
        navigate('/add-availability/locations', { state: { type } });
    }, [navigate]);

    const onViewLogs = useCallback(() => {
        setShowLogsModal(true);
    }, []);

    const onKicioskCapture = useCallback(() => {
        setShowKioskStatusModal(true);
    }, []);

    // Memoize handleDatesSet callback
    const handleDatesSet = useCallback((arg: DatesSetArg) => {
        setStateDate(moment(arg.startStr).format('YYYY-MM-DD'));
        setEndDate(moment(arg.endStr).subtract(1, 'days').format('YYYY-MM-DD'));
    }, []);

    const handleDateRangeChange = useCallback((newStartDate: string, newEndDate: string) => {
        setStateDate(newStartDate);
        setEndDate(newEndDate);
    }, []);

    const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ' ' && searchText.length === 0) {
            event.preventDefault();
        }
    }, [searchText]);

    // const handleInputChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    //     const { value } = e.target as HTMLInputElement;
    //     setSearchText(value);
    //     setIsClicked(true);
    // }, []);

    const handleInputChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const { value } = e.target as HTMLInputElement;
        const trimmedValue = value.replace(/^\s+/, '');

        setSearchText(trimmedValue);
        setIsClicked(true);
    }, []);

    // Memoize filtered locations
    const filteredLocations = useMemo(() => {
        if (!AvailabilityList || !Array.isArray(AvailabilityList)) return [];
        return AvailabilityList.filter((loc: AvailabilityList) =>
            loc.name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [AvailabilityList, searchText]);

    return (
        <>
            {showToast && <Toast type="delete" message={toastMessage} />}
            {/* Full-screen loader only for permissions / location list — not visits/availability refetches (those block the whole page). */}
            {(permissionLoading || locationsListLoader) && <Loader />}
            {canCreateAvailability && addAvailability && (
                <CustModal
                    open={addAvailability}
                    setOpen={setAddAvailability}
                    bodyData={
                        <AddAvailability
                            setOpen={setAddAvailability}
                            AvailabilityList={AvailabilityList}
                            setShowToast={setShowToast}
                            setToastMessage={setToastMessage}
                        />
                    }
                />
            )}

            {(canCreateAppointment ||
                canCreateAvailability ||
                canReadAppointment ||
                canReadAvailability ||
                canDeleteAppointment ||
                canDeleteAvailability ||
                canUpdateAppointment ||
                canUpdateAvailability) && (
                    <div className={styles.container}>
                        <div className={styles.panel}>
                            {canCreateAvailability && (
                                <div className={styles.btnWrapper}>
                                    <button onClick={() => addAvailabilityHandler('add-availability')}>+ Add Availability</button>
                                </div>
                            )}

                            {canDeleteAvailability && (
                                <div className={styles.btnMakeUnitUnavailable}>
                                    <button onClick={() => addAvailabilityHandler('make-unit-unavailable')}>Make Unit Unavailable</button>
                                </div>
                            )}

                            <div className={styles.propertySearchWrapper}>
                                <div>
                                    <div className={styles.searchBox}>
                                        <div className={styles.icon}>
                                            <SearchIcon className={styles.searchIcon} fill="#191c21" />
                                        </div>
                                        <input
                                            value={searchText || ''}
                                            onKeyPress={handleKeyPress}
                                            onChange={handleInputChange}
                                            type="text"
                                            placeholder={'Search Property'}
                                            maxLength={50}
                                            className={styles.inputBox}
                                        />
                                    </div>
                                </div>
                            </div>

                            <ul className={styles.unitList}>
                                {locationsListLoader ? (
                                    <TinyLoader />
                                ) : filteredLocations.length > 0 ? (
                                    filteredLocations.map((loc: AvailabilityList) => (
                                        <li
                                            className={
                                                !locationId
                                                    ? filteredLocations[0].id === loc.id
                                                        ? styles.locationName
                                                        : ''
                                                    : locationId === loc.id
                                                        ? styles.locationName
                                                        : ''
                                            }
                                            onClick={() => {
                                                setLocationId(loc.id);
                                                setLocationName(loc.name);
                                            }}
                                            key={loc.id}>
                                            {loc.name}
                                        </li>
                                    ))
                                ) : (
                                    <div className={styles.noRecordsContent}>
                                        <p className={styles.noResult}>No properties found</p>
                                    </div>
                                )}
                            </ul>

                            <ul className={styles.lockerColorList}>
                                {Array(selectedLocationLockersCount)
                                    .fill(0)
                                    .map((_, index) => (
                                        <li id={styles[`locker${index + 1}`]} key={index}>
                                            <span></span>Locker {index + 1}
                                        </li>
                                    ))}
                            </ul>
                        </div>

                        <div className={styles.calendar}>
                            <div className={styles.calendarContainer}>
                                <div className={styles.calendarHeader}>
                                    <div className={styles.headerLeft}>
                                        <div className={styles.calendarSwitcher}>
                                            <div className={styles.btnWrapper}>
                                                {canReadAppointment && (
                                                    <button
                                                        className={active === 'appointments' ? styles.btnGreen : ''}
                                                        onClick={() => setActive('appointments')}>
                                                        Appointments
                                                    </button>
                                                )}
                                                {canReadAvailability && (
                                                    <button
                                                        className={active === 'availibility' ? styles.btnGreen : ''}
                                                        onClick={() => setActive('availibility')}>
                                                        Availability
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {(SHOW_KIOSK_STATUS_BUTTON && !!locationId && canReadAvailability && userInfo?.role === 'super_admin') && (
                                            <div className={styles.btnContainer}>
                                                {!!locationId && (
                                                    <Button
                                                        className={`${styles.outlinedBtn} ${styles.kioskCaptureBtn}`}
                                                        variant="outlined-green"
                                                        onClick={onKicioskCapture}>
                                                        <KeyLockerIcon width={20} height={20} color="currentColor" fill="none" />
                                                        <span>Kiosk Status</span>
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.headerRight}>
                                        {(!!locationId && (canReadAppointment || canReadAvailability)) && (
                                            <div className={styles.viewModeSwitcher}>
                                                <div className={styles.btnWrapper}>
                                                    <button
                                                        className={viewMode === 'list' ? styles.btnGreen : ''}
                                                        onClick={() => setViewMode('list')}>
                                                        List
                                                    </button>
                                                    <button
                                                        className={viewMode === 'calendar' ? styles.btnGreen : ''}
                                                        onClick={() => setViewMode('calendar')}>
                                                        Calendar
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {(!!locationId && canReadAvailability) && (
                                            <div className={styles.btnContainer}>
                                                {isBMSProperty(locationId) && (
                                                    <button className={styles.lockerBtn} onClick={() => setShowLockerManagementModal(true)}>
                                                        Locker Management
                                                    </button>
                                                )}

                                                {!!locationId && (
                                                    <Button className={styles.outlinedBtn} variant="outlined-green" onClick={onViewLogs}>
                                                        <HistoryIcon fill="#51ab86" />
                                                        <span>View Logs</span>
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.appointmentsCalendar}>
                                    {active === 'appointments' && (
                                        <Calendar
                                            key={locationId}
                                            lockersCount={selectedLocationLockersCount}
                                            locationId={locationId}
                                            handleDatesSet={handleDatesSet}
                                            handleDateRangeChange={handleDateRangeChange}
                                            viewMode={viewMode}
                                        />
                                    )}
                                    {active === 'availibility' && (
                                        <AvailabilityFullCalendar
                                            key={locationId}
                                            lockersCount={selectedLocationLockersCount}
                                            locationId={locationId}
                                            handleDatesSet={handleDatesSet}
                                            handleDateRangeChange={handleDateRangeChange}
                                            viewMode={viewMode}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            <CustModal
                open={showLogsModal}
                className="logs-modal"
                setOpen={setShowLogsModal}
                bodyData={
                    <AppointmentAvailabilityLogs
                        setShowModal={setShowLogsModal}
                        startDate={startDate}
                        endDate={endDate}
                        locationId={locationId}
                        locationName={locationName}
                    />
                }
            />

            <CustModal
                open={showKioskStatusModal}
                className="kiosk-capture-modal"
                setOpen={setShowKioskStatusModal}
                bodyData={
                    <KioskStatus
                        setShowModal={setShowKioskStatusModal}
                        locationId={locationId}
                        locationName={locationName}
                    />
                }
            />

            <CustModal
                open={showLockerManagementModal}
                setOpen={setShowLockerManagementModal}
                bodyData={
                    <LockerManageMent setShowModal={setShowLockerManagementModal} locationId={locationId} locationName={locationName} />
                }
            />
        </>
    );
};

export default Visits;
